"use client";

import React from "react";
import { ArrowUpRight, ShieldCheck } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
const products = [{
  id: "1",
  title: "QuickDetect Trace Explosives",
  category: "Trace Detection",
  description: "Handheld trace explosive detection system engineered for rapid screening and high-sensitivity field identification with minimal sample preparation.",
  link: "/QuickDetectTraceExplosivesPage",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/quickdetect_trace_explosives.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/quickdetect_trace_explosives.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/quickdetect_trace_explosives.png-wvc-sizes",
  imageAlt: "QuickDetect Trace Explosives Analyzer"
}, {
  id: "2",
  title: "CQL Max-ID",
  category: "Chemical Identification",
  description: "Handheld 1064 nm Raman spectrometer for chemical threat identification, penetrating colored containers and overcoming sample fluorescence.",
  link: "/CqlMaxIdPage",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/cql_max_id_analyzer.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/cql_max_id_analyzer.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/cql_max_id_analyzer.png-wvc-sizes",
  imageAlt: "CQL Max-ID Raman Analyzer"
}, {
  id: "3",
  title: "Rigaku ResQ CQL Platform",
  category: "Field Raman",
  description: "Field-ready 1064 nm Raman platform designed for border security, defense, and emergency responders analyzing hazardous chemicals and narcotics.",
  link: "/RigakuResQCQLPlatformPage",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/rigaku_resq_cql_platform.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/rigaku_resq_cql_platform.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/rigaku_resq_cql_platform.png-wvc-sizes",
  imageAlt: "Rigaku ResQ CQL Platform Instrument"
}, {
  id: "4",
  title: "Safety & Security Accessories and Sampling",
  category: "Accessories & Tools",
  description: "Tactical sampling tools, protective holders, calibration kits, and adapters built to support rigorous field analytical workflows.",
  link: "/SafetySecurityAccessoriesAndSamplingPage",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/safety_security_accessories.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/safety_security_accessories.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/safety_security_accessories.png-wvc-sizes",
  imageAlt: "Analytical Sampling Kits and Security Accessories"
}];
export default function ProductShowcase() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="productshowcase" data-nav="light" className="bg-background text-foreground py-24 md:py-32">
      <div className="page-container">
        {/* Split 8/4 Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-16 pb-8 border-b border-border">
          <div className="lg:col-span-8 flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="px-3 py-1 text-xs gap-1.5">
                <ShieldCheck className="size-3.5 text-primary" />
                <span>Safety & Security Solutions</span>
              </Badge>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-semibold tracking-tight text-foreground">
              Specialized Analytical Instruments
            </h2>
          </div>

          <div className="lg:col-span-4 flex flex-col justify-end gap-5">
            <p className="text-base text-muted-foreground leading-relaxed">
              Application-led analytical technology and rugged field spectrometers
              engineered for regulated testing, first response, and defense operations.
            </p>
            <div>
              <Button variant="outline" size="sm" asChild>
                <Link to="/ProductsPage" className="group inline-flex items-center gap-2">
                  <span>View All Products</span>
                  <ArrowUpRight className="size-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* 4-Column Product Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {products.map((item, i) => <motion.div key={item.id} data-index={i} initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-40px"
        }} transition={{
          duration: 0.4,
          delay: i * 0.08,
          ease: "easeOut"
        }} className="flex flex-col h-full">
              <Card className="flex flex-col h-full border border-border bg-card text-card-foreground rounded-xl overflow-hidden hover:shadow-md transition-[box-shadow] duration-200">
                <div className="relative border-b border-border bg-muted">
                  <AspectRatio ratio={16 / 11}>
                    <img src={item.imageSrc} data-wvc-srcset={item.imageSrcset} data-wvc-sizes={item.imageSizes} alt={item.imageAlt} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                  </AspectRatio>
                </div>

                <CardHeader className="pt-5 pb-3">
                  <div className="mb-2">
                    <Badge variant="outline" className="text-xs font-normal text-muted-foreground">
                      {item.category}
                    </Badge>
                  </div>
                  <CardTitle className="text-lg font-default font-semibold leading-snug text-card-foreground">
                    {item.title}
                  </CardTitle>
                </CardHeader>

                <CardContent className="flex-1 pb-4">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </CardDescription>
                </CardContent>

                <CardFooter className="pt-0 pb-5">
                  <Button variant="secondary" size="sm" className="w-full justify-between" asChild>
                    <Link to={item.link}>
                      <span>Explore Specifications</span>
                      <ArrowUpRight className="size-4" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </div>
      </div>
    </section>;
}